import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { thumb } from "@/lib/img";
import useSEO from "@/hooks/useSEO";
import { BLOG_POSTS } from "@/data/blogData";
import { useBlogPosts } from "@/hooks/useBlog";
import { organizationSchema, breadcrumbSchema, itemListSchema } from "@/lib/seo-schemas";
import QuickQuote from "@/components/QuickQuote";

const BlogPage = () => {
  const posts = useBlogPosts();
  useSEO({
    title: "Exhibition Stand Cost Guides, Expo Checklists & Booth Design Advice | Stagehook Global Blog",
    description: "Transparent exhibition stand pricing for the USA, Germany, Italy and Russia, exhibitor checklists for Prodexpo, SMM Hamburg, Salone del Mobile and Las Vegas shows, and booth design trends — from a builder with 850+ stands delivered.",
    keywords: "exhibition stand cost, trade show booth cost, exhibitor guide, booth design trends, exhibition stand builder blog",
    path: "/blog",
    jsonLd: [
      organizationSchema(),
      breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }]),
      itemListSchema("Stagehook Global Blog", BLOG_POSTS.map((p) => ({ name: p.title, path: `/blog/${p.slug}` }))),
    ],
  });

  return (
    <main data-testid="blog-page" className="pt-[100px] pb-24">
      <div className="shg-container">
        <div className="max-w-3xl mb-14">
          <div className="shg-eyebrow mb-5">Insights for exhibitors</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-6">Stand costs, expo checklists<br />and <span style={{ color: "var(--red)" }}>design that sells</span>.</h1>
          <p className="text-lg text-[var(--text-soft)]">Real 2026 pricing per sqm and sq ft, venue rules, deadlines and lessons from 850+ stands across the USA, Europe, Russia and Asia.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {posts.map((p, i) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="shg-card group block fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="aspect-[16/10] overflow-hidden">
                <img src={thumb(p.heroImage)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] uppercase mb-3">
                  <span style={{ color: "var(--red)" }}>{p.category}</span><span>·</span><span className="flex items-center gap-1"><Clock size={11} />{p.readTime} min</span>
                </div>
                <h2 className="font-display text-xl leading-tight group-hover:text-[var(--red)] transition-colors mb-3">{p.title}</h2>
                <p className="text-sm text-[var(--text-soft)] line-clamp-3">{p.description}</p>
                <div className="mt-4 text-xs font-mono tracking-[0.15em] text-[var(--red)] flex items-center gap-1">READ <ArrowUpRight size={12} /></div>
              </div>
            </Link>
          ))}
        </div>

        <div className="max-w-3xl"><QuickQuote source="blog-index" /></div>
      </div>
    </main>
  );
};

export default BlogPage;
